/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AlertConfigResponse, AlertConfigUpdateRequest, ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

type UpdateAlertConfigMutationConfig = {
  onSuccess?: (data: AlertConfigResponse) => void;
  onError?: (error: ApiError) => void;
};

export function useUpdateAlertConfigMutation({ onSuccess, onError }: UpdateAlertConfigMutationConfig = {}) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<
    AlertConfigResponse,
    ApiError,
    { id: string; requestBody: AlertConfigUpdateRequest }
  >({
    mutationFn: ({ id, requestBody }) => {
      return mgmtPlaneAPIClient.alerts.updateAlertConfig({
        tenantId: selectedTenantId!,
        id,
        requestBody,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alertConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['alertConfig', selectedTenantId, variables.id] });
      onSuccess?.(data);
    },
    onError,
  });

  return { mutate, isError, error, isPending };
}
